import React from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Toolbar, Divider, Typography, Box } from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { IconButton, Menu, MenuItem, Avatar } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { getUserAcc, clearTokens } from "../Auth/AuthUtil";
import BiotechIcon from '@mui/icons-material/Biotech';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssessmentIcon from '@mui/icons-material/Assessment';

const drawerWidth = 240;
const collapsedWidth = 64;


const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded?: boolean }>(({ theme, expanded = false }) => ({
  width: expanded ? drawerWidth : collapsedWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: expanded ? drawerWidth : collapsedWidth,
    boxSizing: 'border-box',
    borderRight: 'none',
    margin: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

interface SidebarProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, handleDrawerToggle, expanded, setExpanded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const user = getUserAcc();

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleSidebar = () => {
    setExpanded(!expanded);
  };


  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Orders', icon: <ShoppingCartIcon />, path: '/orders' },
    { text: 'Coupons', icon: <LocalOfferIcon />, path: '/Coupons-section' },
    { text: 'Send Notification', icon: <NotificationsIcon />, path: '/send-notification' },
    { text: 'Consultations', icon: <VaccinesIcon />, path: '/consultations' },
    { text: 'Subscriptions', icon: <SubscriptionsIcon />, path: '/subscriptions' },
    { text: 'App Users', icon: <PeopleAltIcon />, path: '/app-users' },
    { text: 'Kiosk Reports', icon: <BarChartIcon />, path: '/kiosk-reports' },
    { text: 'Food Log', icon: <RestaurantIcon />, path: '/food-log' },
    { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
    { text: 'Lab Tests', icon: <BiotechIcon />, path: '/lab-reports' },
    { text: 'Clinical Screening', icon: <AssessmentIcon />, path: '/clinical-screening' },
    { text: 'Reports', icon: <BarChartIcon />, path: '/reports' },
    { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
  ];

  const handleLogout = () => {
    try {
      clearTokens();
    } finally {
      handleClose();
      navigate("/login", { replace: true });
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Toolbar sx={{ bgcolor: '#f4ecd6', px: [1] }}>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: expanded ? 'space-between' : 'center', px: expanded ? 1 : 0 }}>
          {expanded && (
            <Box component="img" src="https://aiwellv2reports.s3.ap-south-1.amazonaws.com/ayurai_logo+(1).png" alt="Ayurai" sx={{ height: 32, objectFit: 'contain' }} />
          )}
          <IconButton onClick={toggleSidebar}>
            {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      </Toolbar>
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2, backgroundColor: '#f9f5f9' }}>
        <List disablePadding>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const activeColor = theme.palette.primary.main;

            return (
              <ListItem
                button
                key={item.text}
                component={RouterLink}
                to={item.path}
                onClick={mobileOpen ? handleDrawerToggle : undefined}
                sx={{
                  mb: 0.5,
                  borderRadius: 2,
                  backgroundColor: isActive ? alpha(activeColor, 0.08) : 'transparent',
                  color: isActive ? activeColor : 'text.primary',
                  justifyContent: expanded ? 'initial' : 'center',
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: isActive ? alpha(activeColor, 0.12) : alpha(activeColor, 0.04),
                  },
                }}
              >
                <ListItemIcon sx={{
                  color: isActive ? activeColor : 'text.secondary',
                  minWidth: 0,
                  mr: expanded ? 2 : 'auto',
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </ListItemIcon>
                {expanded && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider />

      <Box sx={{ p: 1.5 }}>
        <Box
          component="button"
          type="button"
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: expanded ? 'flex-start' : 'center',
            p: 1,
            borderRadius: 2,
            cursor: 'pointer',
            // bgcolor: open ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: '1rem'
            }}
          >
            {user?.name?.[0]?.toUpperCase?.() || "U"}
          </Avatar>

          {expanded && (
            <Box sx={{ ml: 2, overflow: 'hidden' }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, color: 'text.primary' }}>
                {user?.name || "User"}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                {user?.email || ""}
              </Typography>
            </Box>
          )}
        </Box>

        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }} // Open upwards usually better for footer menus
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              minWidth: 200,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: 2,
              mb: 1
            }
          }}
        >

          {!expanded && (
            <Box sx={{ px: 2, py: 1, outline: 'none' }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>{user?.name}</Typography>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>{user?.email}</Typography>
              <Divider sx={{ mt: 1 }} />
            </Box>
          )}

          <MenuItem component={RouterLink} to="/account" onClick={handleClose}>
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            My Account
          </MenuItem>
          <MenuItem component={RouterLink} to="/settings" onClick={handleClose}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <>
      <StyledDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: expanded ? drawerWidth : collapsedWidth },
        }}
        expanded={expanded}
      >
        {drawer}
      </StyledDrawer>
      <StyledDrawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: expanded ? drawerWidth : collapsedWidth },
        }}
        open
        expanded={expanded}
      >
        {drawer}
      </StyledDrawer>
    </>
  );
};

export default Sidebar;
